import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { LEARN_LESSONS } from '@/constants/learnCurriculum';

const PREFIX = 'gab.learn.v1.';
const DRAFT_PREFIX = 'gab.learn.draft.v1.';
const slugs = new Set(LEARN_LESSONS.map(lesson => lesson.slug));
const ProgressContext = createContext({ completed: {}, drafts: {}, ready: false, saved: true, draftsSaved: false, lastLesson: null, visit() {}, toggle() {}, saveDraft() {} });

// Independent keys prevent open tabs from overwriting other lessons' progress.
export function LearnProgressProvider({ children }) {
  const [completed, setCompleted] = useState({});
  const [lastLesson, setLastLesson] = useState(null);
  const [ready, setReady] = useState(false);
  const [saved, setSaved] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [draftsSaved, setDraftsSaved] = useState(true);
  useEffect(() => {
    function readProgress() {
      try {
        const next = {};
        slugs.forEach(slug => { if (localStorage.getItem(PREFIX + slug) === '1') next[slug] = true; });
        setCompleted(next);
        const last = localStorage.getItem(PREFIX + 'last');
        setLastLesson(slugs.has(last) ? last : null);
      } catch { setSaved(false); }
    }
    function readDrafts() {
      try {
        const next = {};
        slugs.forEach(slug => {
          const value = localStorage.getItem(DRAFT_PREFIX + slug);
          if (value !== null) next[slug] = value;
        });
        setDrafts(next);
      } catch { setDraftsSaved(false); }
    }
    readDrafts();
    readProgress();
    setReady(true);
    const sync = event => {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (!event.key || event.key.startsWith(PREFIX)) readProgress();
      if (!event.key) readDrafts();
      else if (event.key.startsWith(DRAFT_PREFIX)) {
        const slug = event.key.slice(DRAFT_PREFIX.length);
        if (slugs.has(slug)) {
          setDrafts(previous => {
            const next = { ...previous };
            if (event.newValue === null) delete next[slug];
            else next[slug] = event.newValue;
            return next;
          });
        }
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  const saveDraft = useCallback((slug, value) => {
    if (!ready || !slugs.has(slug) || typeof value !== 'string') return;
    setDrafts(previous => ({ ...previous, [slug]: value }));
    try { localStorage.setItem(DRAFT_PREFIX + slug, value); }
    catch { setDraftsSaved(false); }
  }, [ready]);
  const visit = useCallback((slug) => {
    if (!slugs.has(slug)) return;
    setLastLesson(slug);
    try { localStorage.setItem(PREFIX + 'last', slug); } catch { setSaved(false); }
  }, []);
  function toggle(slug) {
    if (!ready || !slugs.has(slug)) return;
    const done = !completed[slug];
    setCompleted(previous => ({ ...previous, [slug]: done }));
    try {
      if (done) localStorage.setItem(PREFIX + slug, '1');
      else localStorage.removeItem(PREFIX + slug);
    } catch { setSaved(false); }
  }
  return <ProgressContext.Provider value={{ completed, lastLesson, ready, saved, visit, toggle, drafts, draftsSaved, saveDraft }}>{children}</ProgressContext.Provider>;
}
export const useLearnProgress = () => useContext(ProgressContext);
